import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { documentScrollHandler } from 'src/utils/scollHandler';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { useBoolean, useIsMobile, usePrevious } from '@sravni/react-utils';

import { pushUserId, sendShareClickEvent, sentReadArticleEvent } from '../../../../helpers/analytics';
import { generateContentFromHtml } from '../../../../helpers/generateContentFromHtml';
import { useArticleLikes } from '../../../../hooks/likes';
import { useAuthors } from '../../../../hooks/news';
import { useCopyText } from '../../../../hooks/useCopyText';
import { useUser } from '../../../../hooks/users';
import { useSpecialOffersVerticalWidget } from '../../../../hooks/useSpecialOffersVerticalWidget';
import { IMaterial } from '../../../../interfaces/news';
import { useWhitelabel } from '../../../../reducers/whitelabel';
import { BorderedBanner } from '../../../BorderedBanner';
import articleStyles from '../Article/article.module.scss';
import { ArticleFooter } from '../ArticleFooter';
import { ArticleHeader } from '../ArticleHeader';
import { PopUpTextError } from '../PopUpTextError';
import { TableOfContent } from '../TableOfContent';

export interface IArticleContentProps {
  material: IMaterial;
  index: number;
  commentsCount?: number;
  commentsRef: React.RefObject<HTMLDivElement>;
  banners: {};
  isLoadingBanners: boolean;
  handleShowAuthPopup: () => void;
  isUserLogged: boolean;
  titleTag?: React.ComponentProps<typeof ArticleHeader>['titleTag'];
  titleRef: React.RefObject<HTMLDivElement>;
}

// if matched returns array [<script>code</script>, code inside that script tag]
const SCRIPT_REGEXP = /<script\b([^>](?!type="application\/json"))*>((.(?!<\/script>)*)*)<\/script>/gim;

export const ArticleContent = memo<IArticleContentProps>(
  ({
    material,
    index,
    commentsCount,
    commentsRef,
    banners = {},
    isLoadingBanners,
    handleShowAuthPopup,
    isUserLogged,
    titleTag,
    titleRef,
  }) => {
    const authors = useAuthors();
    const profile = useUser();
    const isMobile = useIsMobile();
    // const isAuthorized = Boolean(profile);

    const [iframeHeight, setIframeHeight] = useState(1000);
    const iframeRef = useRef(null);
    const articleRef = useRef(null);
    const readBreakpoints = useRef({
      quater: false,
      half: false,
      threeQuater: false,
      full: false,
    });

    const articleContentRef = useRef<HTMLDivElement>();
    const externalArticleUid = useRef((Date.now() * Math.random()).toString(36).replace('.', ''));

    const { toggleLike, likesCount, isArticleLiked } = useArticleLikes(material.id);
    const { showTableOfContents: showTableOfContent } = material;

    const isWhitelabel = useWhitelabel();

    const showAds = !material.hideAds;

    const previousProfile = usePrevious(profile);

    const isAuthor = authors.some((author) => {
      return String(author.id) === profile?.sub;
    });

    useEffect(() => {
      if (isEqual(previousProfile, profile) || !profile?.sub) return;
      pushUserId(profile);
    }, [previousProfile, profile]);

    const onScroll = (e, isAuto?: boolean) => {
      if (!articleRef.current) return;
      const scrollTop = e.target.documentElement.scrollTop;
      const articleHeight = articleRef.current.offsetHeight;
      const articleOffsetTop = articleRef.current.offsetTop;

      const top = articleRef.current.getBoundingClientRect().top;

      const quater = Math.round((articleHeight + articleOffsetTop) * 0.25);
      const half = Math.round((articleHeight + articleOffsetTop) * 0.5);
      const threeQuater = Math.round((articleHeight + articleOffsetTop) * 0.75);
      const full = Math.round(articleHeight + articleOffsetTop);

      if (scrollTop >= quater && top <= 0 && !readBreakpoints.current.quater) {
        readBreakpoints.current.quater = true;
        sentReadArticleEvent('25%', isAuto);
      }
      if (scrollTop >= half && top <= 0 && !readBreakpoints.current.half) {
        readBreakpoints.current.half = true;
        sentReadArticleEvent('50%', isAuto);
      }
      if (scrollTop >= threeQuater && top <= 0 && !readBreakpoints.current.threeQuater) {
        readBreakpoints.current.threeQuater = true;
        sentReadArticleEvent('75%', isAuto);
      }
      if (scrollTop >= full && top <= 0 && !readBreakpoints.current.full) {
        readBreakpoints.current.full = true;
        sentReadArticleEvent('100%', isAuto);
      }
    };

    useEffect(() => {
      if (!material.externalLink) {
        documentScrollHandler.sub(onScroll);
        return () => documentScrollHandler.unsub(onScroll);
      }
    }, [material.externalLink]);

    useEffect(() => {
      if (material.externalLink) {
        const handler = ({ data }) => {
          if (data.uid === externalArticleUid.current) {
            setIframeHeight((prevState) => (prevState !== data.height ? data.height : prevState));
          }
        };
        const handlerScroll = () => {
          const _pageYOffset = window.parent.pageYOffset;
          const _viewPortHeight = window.innerHeight;
          const _offsetTop = iframeRef.current.getBoundingClientRect().top; //.offsetTop;

          const sendMessageIframe = JSON.stringify({
            pageYOffset: _pageYOffset,
            viewPortHeight: _viewPortHeight,
            offsetTop: _offsetTop,
          });

          iframeRef.current.contentWindow.postMessage(
            {
              type: 'placementEndlessScroll',
              message: sendMessageIframe,
            },
            '*',
          );
        };

        iframeRef.current.addEventListener('load', handlerScroll);
        window.addEventListener('message', handler);
        window.addEventListener('scroll', handlerScroll);
        return () => window.removeEventListener('message', handler);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [material.externalLink, iframeRef.current]);

    const [getSelectionText, createTimeTextareaCopy] = useCopyText();

    useEffect(() => {
      if (!material.externalLink) {
        articleContentRef.current.addEventListener('copy', () => {
          const { text, status } = getSelectionText();
          if (status && !isAuthor) {
            navigator.clipboard
              .writeText(text)
              .then(() => {
                createTimeTextareaCopy(text);
              })
              .catch(() => {
                createTimeTextareaCopy(text);
              });
          }
        });
      }
    }, [articleContentRef, createTimeTextareaCopy, getSelectionText, isAuthor, material.externalLink]);

    const { htmlBody, scripts } = useMemo(() => {
      let _htmlBody = material.htmlBody;
      const _scripts: Array<string> = [];

      while (true) {
        const match = SCRIPT_REGEXP.exec(_htmlBody);
        if (!match) break;

        _htmlBody = _htmlBody.replace(match[0], '');
        _scripts.push(match[2]);
      }

      return { htmlBody: _htmlBody, scripts: _scripts };
    }, [material]);

    useEffect(() => {
      scripts.map(window.eval);
    }, [scripts]);

    const [, rerender] = useBoolean(false);
    const wasFirstRender = useRef<boolean>(false);

    const { content, withSOWidget } = generateContentFromHtml(htmlBody);
    useSpecialOffersVerticalWidget(articleRef.current, withSOWidget);

    useEffect(() => {
      if (wasFirstRender.current || !withSOWidget) return;
      wasFirstRender.current = true;

      rerender.toggle();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withSOWidget]);

    const handleShareClick = (_, name) => {
      sendShareClickEvent(name);
    };

    return material.externalLink ? (
      <>
        <div ref={titleRef}></div>
        <iframe
          sandbox="allow-same-origin allow-scripts allow-popups"
          ref={iframeRef}
          src={
            material.externalLink.includes('special/pik')
              ? `${material.externalLink}?wl=true&uid=${externalArticleUid.current}&int_source=bs&int_medium=pik&utm_medium=bs`
              : `${material.externalLink}?wl=true&uid=${externalArticleUid.current}`
          }
          width="100%"
          height={`${iframeHeight}px`}
          frameBorder="0"
          scrolling="no"
        />
      </>
    ) : (
      <>
        <article className={articleStyles.wrapper} ref={articleRef}>
          <ArticleHeader
            likesCount={likesCount}
            isArticleLiked={isArticleLiked}
            onToggleLike={toggleLike}
            index={index}
            material={material}
            commentsRef={commentsRef}
            titleTag={titleTag}
            titleRef={titleRef}
            onShareClick={handleShareClick}
          />
          {!isWhitelabel && showAds && (isLoadingBanners || banners?.[BANNER_TYPES.TGBM_7]) && (
            <div className={isMobile ? 'h-mt-28 h-mb-28' : 'h-mt-32 h-mb-32'}>
              <BorderedBanner
                banner={banners?.[BANNER_TYPES.TGBM_7]}
                type={BANNER_TYPES.TGBM_7}
                className={articleStyles.bannerFullWidthMobile}
              />
            </div>
          )}
          {showTableOfContent && <TableOfContent articleContent={articleContentRef?.current} articleIndex={index} />}
          <div className={articleStyles.articleContentWrapper}>
            <PopUpTextError />
            <div
              ref={articleContentRef}
              className={classNames(articleStyles.articleContent, 'article-body h-mt-24')}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </div>
          <ArticleFooter
            likesCount={likesCount}
            isArticleLiked={isArticleLiked}
            onToggleLike={toggleLike}
            material={material}
            commentsCount={commentsCount}
            commentsRef={commentsRef}
            handleShowAuthPopup={handleShowAuthPopup}
            isUserLogged={isUserLogged}
            onShareClick={handleShareClick}
          />
        </article>
      </>
    );
  },
);
